import Bowser from 'bowser';

/**
 * Class used for device detection or for getting certain info about the player's device.
 */
export class DeviceInfo {

  static parser = (() => Bowser.getParser(window.navigator.userAgent))();

  /**
   * @typedef {Object} BrowserInfo
   * @property {Object} browser - Information about the user's browser.
   * @property {string} browser.name - The name of the browser.
   * @property {string} browser.version - The version of the browser.
   * @property {Object} os - Information about the user's operating system.
   * @property {string} os.name - The name of the operating system.
   * @property {string} os.version - The version of the operating system.
   * @property {string} os.versionName - The version name of the operating system.
   * @property {Object} platform - Information about the user's platform.
   * @property {string} platform.type - The type of platform (e.g., "desktop").
   * @property {Object} engine - Information about the rendering engine.
   * @property {string} engine.name - The name of the rendering engine.
   * @property {string} engine.version - The version of the rendering engine.
   */

  /**
   * Information about the user's device and browser.
   * @type {BrowserInfo}
   */
  static info = (() => { Bowser.parse(window.navigator.userAgent); })();

  /**
   * `true` if the device is a mobile phone or tablet.
   * @type {boolean}
   */
  static isHandheld = [ 'tablet', 'mobile' ].includes(this.parser.getPlatformType());

  /**
   * `true` if the device is a desktop computer.
   * @type {boolean}
   */
  static isDesktop = !this.isHandheld;

  /**
   * `true` if the device is a tablet.
   * @type {boolean}
   */
  static isTablet = this.parser.getPlatformType() === 'tablet';

  /**
   * `true` if the device is a mobile.
   * @type {boolean}
   */
  static isMobile = this.parser.getPlatformType() === 'mobile';

  /**
   * The type of platform: "desktop," "tablet," or "mobile."
   * @type {('desktop' | 'mobile' | 'tablet')}
   */
  static platform = this.parser.getPlatformType();

  /**
   * `true` if the device is from Apple.
   * @type {boolean}
   */
  // static isApple = !!this.info.platform?.vendor;
  static isApple = this.parser.getPlatform().vendor;

  /**
   * `true` if the device is running iOS.
   * @type {boolean}
   */
  static isIOS = this.parser.getOSName() === 'iOS';

  /**
   * `true` if the device is running Android.
   * @type {boolean}
   */
  static isAndroid = this.parser.getOSName() === 'Android';

  /**
   * `true` if the device is running Windows.
   * @type {boolean}
   */
  static isWindows = this.parser.getOSName() === 'Windows';

  /**
   * `true` if the device is running macOS.
   * @type {boolean}
   */
  static isMacOS = this.parser.getOSName() === 'macOS';

  /**
   * `true` if the device is running Linux.
   * @type {boolean}
   */
  static isLinux = this.parser.getOSName() === 'Linux';

  /**
   * The name of the operating system (e.g., "macOS").
   * @type {string}
   */
  static osName = this.parser.getOSName();

  /**
   * The version of the operating system (e.g., "10.12.6").
   * @type {string}
   */
  static osVersion = this.parser.getOSVersion();

  /**
   * `true` if the player's browser is Safari.
   * @type {boolean}
   */
  static isSafari = this.parser.getBrowserName() === 'Safari';

  /**
   * `true` if the player's browser is Google Chrome.
   * @type {boolean}
   */
  static isChrome = this.parser.getBrowserName() === 'Chrome';

  /**
   * `true` if the player's browser is Opera.
   * @type {boolean}
   */
  static isOpera = this.parser.getBrowserName() === 'Opera';

  /**
   * `true` if the player's browser is Firefox.
   * @type {boolean}
   */
  static isFirefox = this.parser.getBrowserName() === 'Firefox';

  /**
   * `true` if the player's browser is Samsung Internet.
   * @type {boolean}
   */
  static isSamsungBrowser = this.parser.getBrowserName() === 'Samsung Internet';

  /**
   * `true` if the player's browser is Internet Explorer or Microsoft Edge.
   * @type {boolean}
   */
  static isMicrosoftBrowser = [ 'Internet Explorer', 'Microsoft Edge' ].includes(this.parser.getBrowserName());

  /**
   * `true` if the player's browser is Microsoft Edge.
   * @type {boolean}
   */
  static isMsEdge = this.parser.getBrowserName() === 'Microsoft Edge';

  /**
   * `true` if the player's browser is Internet Explorer
   * @type {boolean}
   */
  static isInternetExplorer = this.parser.getBrowserName() === 'Internet Explorer';

  /**
   * The name of the player's browser (e.g., "Chrome").
   * @type {string}
   */
  static browser = this.parser.getBrowserName();

  /**
   * The version of the player's browser (e.g., "32.0").
   * @type {string}
   */
  static browserVersion = this.parser.getBrowserVersion();

}
